import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger, 
  DropdownMenuSeparator 
} from "@/components/ui/dropdown-menu";
import { Calendar as CalendarIcon, ChevronDown, ChevronUp, ChevronsUpDown, X, Filter, Plus, Grid, List } from 'lucide-react';
import { ColumnVisibilityFilter, type ColumnConfig } from './ColumnVisibilityFilter';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { Calendar } from "@/components/ui/calendar";
import { sampleOptionCounts, realFilterCounts } from "../utils/filterCounts";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useLayoutContext } from '@/contexts/LayoutContext';

// Mobile Filter Bar Component
const MobileFilterBar = ({ 
  filters, 
  filterOptions, 
  handleFilterChange, 
  clearAllFilters,
  dateRange,
  onDateRangeChange,
  clearDateRange,
  filteredCount,
  hideCreateNew = false,
  viewMode,
  onViewModeChange,
  showViewSelector = true,
  groupByUrl,
  onGroupByUrlChange,
  columnVisibility,
  onColumnVisibilityChange
}: {
  filters: Record<string, string[]>;
  filterOptions: Record<string, string[]>;
  handleFilterChange: (field: string, value: string, checked: boolean) => void;
  clearAllFilters: () => void;
  dateRange?: { startDate?: string; endDate?: string };
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  clearDateRange: () => void;
  filteredCount: number;
  hideCreateNew?: boolean;
  viewMode?: 'list' | 'grid';
  onViewModeChange?: (mode: 'list' | 'grid') => void;
  showViewSelector?: boolean;
  groupByUrl?: boolean;
  onGroupByUrlChange?: (grouped: boolean) => void;
  columnVisibility?: ColumnConfig[];
  onColumnVisibilityChange?: (columns: ColumnConfig[]) => void;
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const { mainScrollRef } = useLayoutContext();

  // Scroll behavior effect - listen to main element scroll (internal scroll)
  useEffect(() => {
    const mainElement = mainScrollRef?.current;
    if (!mainElement) {
      console.log('[MobileFilterBar] Main element ref not found from context');
      return;
    }

    console.log('[MobileFilterBar] Setting up scroll listener on main element');

    const handleScroll = () => {
      const currentScrollY = mainElement.scrollTop;
      const isMobile = window.innerWidth < 768; // md breakpoint
      
      console.log('[MobileFilterBar] Scroll event:', {
        currentScrollY,
        lastScrollY,
        isMobile,
        isVisible,
        scrollDirection: currentScrollY > lastScrollY ? 'down' : 'up'
      });
      
      // Skip minimal scroll changes but use a smaller threshold
      if (Math.abs(currentScrollY - lastScrollY) < 5) return;
      
      if (!isMobile) {
        // Ensure filter bar is shown and exit if on desktop
        if (!isVisible) setIsVisible(true); 
        return;
      }

      // Improved mobile scroll logic - same as MainLayout
      if (currentScrollY > lastScrollY && currentScrollY > 20) {
        // Scrolling Down - hide filter bar
        console.log('[MobileFilterBar] Hiding filter bar - scrolling down');
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY || currentScrollY <= 20) {
        // Scrolling Up or at the top - show filter bar
        console.log('[MobileFilterBar] Showing filter bar - scrolling up');
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY <= 0 ? 0 : currentScrollY); // Update last scroll position
    };

    // Use requestAnimationFrame for smoother performance
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    mainElement.addEventListener('scroll', onScroll, { passive: true });
    console.log('[MobileFilterBar] Scroll listener attached to main element');

    return () => {
      mainElement.removeEventListener('scroll', onScroll);
      console.log('[MobileFilterBar] Scroll listener removed from main element');
    };
  }, [lastScrollY, isVisible, mainScrollRef]); // Rerun effect if these change - same as MainLayout

  // Get all brands for horizontal display (no limit)
  const allBrands = useMemo(() => {
    return (filterOptions.brandName || []);
  }, [filterOptions.brandName]);

  const selectedBrandsCount = filters.brandName?.length || 0;
  const totalActiveFilters = Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  return (
    <>
      {/* Mobile Filter Bar - Positioned within page flow with sticky behavior */}
      <div className={`lg:hidden sticky top-[60px] left-0 right-0 z-40 bg-white border-b border-gray-200 transition-transform duration-200 ease-in-out shadow-sm ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`} style={{ willChange: 'transform' }}>
        <div className="flex items-center px-3 py-3 gap-2 overflow-x-auto no-scrollbar max-w-full w-full">
          {/* Refine Button */}
          <Sheet open={isMobileSheetOpen} onOpenChange={setIsMobileSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="flex-shrink-0 relative text-xs px-2 py-2 bg-white hover:bg-gray-50 min-w-fit">
                <Filter className="h-3 w-3 mr-1" />
                Refine
                {totalActiveFilters > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                    {totalActiveFilters}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] p-0">
              <SheetHeader className="px-4 py-4 border-b">
                <SheetTitle>Filter by</SheetTitle>
              </SheetHeader>
              <SideFilterSheet
                isVisible={true}
                filters={filters}
                filterOptions={filterOptions}
                handleFilterChange={handleFilterChange}
                clearAllFilters={clearAllFilters}
                savedFilters={{}}
                currentFilterName=""
                newFilterName=""
                setNewFilterName={() => {}}
                saveCurrentFilter={() => {}}
                loadSavedFilter={() => {}}
                setDefaultFilter={() => {}}
                deleteSavedFilter={() => {}}
                dateRange={dateRange}
                onDateRangeChange={onDateRangeChange}
                clearDateRange={clearDateRange}
                filteredCount={filteredCount}
                optionCounts={{}}
                dynamicCounts={{}}
                isMobileSheet={true}
                hideCreateNew={hideCreateNew}
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
                showViewSelector={showViewSelector}
                groupByUrl={groupByUrl}
                onGroupByUrlChange={onGroupByUrlChange}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={onColumnVisibilityChange}
              />
            </SheetContent>
          </Sheet>

          {/* Brand Filters */}
          <div className="flex items-center gap-1 flex-shrink-0 min-w-0">
            {allBrands.map((brand: string) => {
              const isSelected = filters.brandName?.includes(brand) || false;
              return (
                <Button
                  key={brand}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFilterChange('brandName', brand, !isSelected)}
                  className={`whitespace-nowrap text-xs px-1.5 py-1.5 h-auto min-w-fit transition-all duration-200 ${
                    isSelected 
                      ? 'bg-[#8A6FE8] hover:bg-[#7A5FD8] text-white border-[#8A6FE8]' 
                      : 'border-gray-300 text-gray-700 hover:border-[#8A6FE8] hover:text-[#8A6FE8] bg-white'
                  }`}
                >
                  {brand}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

// --- Updated Date Range Dropdown Placeholder ---
const DateRangeDropdownPicker = ({ startDate, endDate, onApply, onClear }: { 
    startDate?: Date, 
    endDate?: Date, 
    onApply: (start?: Date, end?: Date, label?: string) => void, 
    onClear: () => void 
}) => {
  
  // State for managing custom range calendar visibility
  const [showCalendar, setShowCalendar] = useState(false);
  // State for the temporary custom range selection
  const [customRange, setCustomRange] = useState<DateRange | undefined>(
    startDate && endDate ? { from: startDate, to: endDate } : undefined
  );

  // Update customRange state if props change (e.g., reset is clicked)
  useEffect(() => {
      setCustomRange(startDate && endDate ? { from: startDate, to: endDate } : undefined);
      // If props are cleared, ensure calendar isn't stuck open
      if (!startDate && !endDate && showCalendar) {
          setShowCalendar(false);
      }
  }, [startDate, endDate, showCalendar]);

  const calculateDate = (daysAgo: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    date.setHours(0, 0, 0, 0); // Start of the day
    return date;
  };

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999); // End of today

  // Generate calendar month options (last 24 months + current month)
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Add current month first
    const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    currentMonthStart.setHours(0, 0, 0, 0); // Start of first day
    const currentMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    currentMonthEnd.setHours(23, 59, 59, 999); // End of last day
    
    options.push({
      label: format(currentDate, 'MMMM yyyy'),
      start: currentMonthStart,
      end: currentMonthEnd
    });
    
    // Add previous months
    for (let i = 1; i <= 24; i++) {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      monthStart.setHours(0, 0, 0, 0); // Start of first day
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      monthEnd.setHours(23, 59, 59, 999); // End of last day
      
      options.push({
        label: format(monthDate, 'MMMM yyyy'),
        start: monthStart,
        end: monthEnd
      });
    }
    
    return options;
  };

  const monthOptions = generateMonthOptions();

  const ranges = [
    { label: "Last 7 Days", start: calculateDate(6), end: todayEnd },
    { label: "Last 30 Days", start: calculateDate(29), end: todayEnd },
    { label: "This Month", start: new Date(todayEnd.getFullYear(), todayEnd.getMonth(), 1), end: todayEnd },
    { label: "Last 90 Days", start: calculateDate(89), end: todayEnd },
    { label: "Last Year", start: new Date(todayEnd.getFullYear() - 1, todayEnd.getMonth(), todayEnd.getDate()), end: todayEnd },
  ];

  const currentLabel = useMemo(() => {
    if (!startDate || !endDate) return "Select Date Range";
    
    // Check for exact month matches first
    for (const monthOption of monthOptions) {
      if (startDate.getTime() === monthOption.start.getTime() && 
          endDate.getTime() === monthOption.end.getTime()) {
        return monthOption.label;
      }
    }
    
    // Check for preset ranges
    for (const range of ranges) {
      // Compare timestamps for reliability
      if (startDate.getTime() === range.start.getTime() && endDate.getTime() === range.end.getTime()) {
        return range.label;
      }
    }
    
    // Format custom range using date-fns
    return `${format(startDate, 'dd/MM/yy')} - ${format(endDate, 'dd/MM/yy')}`;
  }, [startDate, endDate, ranges, monthOptions]);

  const handleApplyCustomRange = () => {
    if (customRange?.from && customRange?.to) {
      // Ensure the end date includes the full day
      const endDateWithTime = new Date(customRange.to);
      endDateWithTime.setHours(23, 59, 59, 999);
      
      const label = `${format(customRange.from, 'dd/MM/yy')} - ${format(customRange.to, 'dd/MM/yy')}`;
      onApply(customRange.from, endDateWithTime, label);
      setShowCalendar(false);
    }
  };

  const handleCancelCustomRange = () => {
    // Reset custom range selection to match current applied range
    setCustomRange(startDate && endDate ? { from: startDate, to: endDate } : undefined);
    setShowCalendar(false);
  };

  const handlePresetSelect = (range: typeof ranges[0]) => {
     onApply(range.start, range.end, range.label);
     setShowCalendar(false); // Ensure calendar closes if open
  };

  const handleMonthSelect = (monthOption: typeof monthOptions[0]) => {
     onApply(monthOption.start, monthOption.end, monthOption.label);
     setShowCalendar(false); // Ensure calendar closes if open
  };

  const handleClear = () => {
    onClear();
    setShowCalendar(false); // Ensure calendar closes
  };

  return (
     <div className="mb-4 sm:mb-6">
       {!showCalendar ? (
          // --- Dropdown View --- 
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className={`w-full justify-between text-sm sm:text-base py-1 sm:py-2 ${
                  startDate && endDate 
                    ? "border-[#8A6FE8] bg-[#8A6FE8]/10 text-[#8A6FE8] hover:bg-[#8A6FE8]/20" 
                    : ""
                }`}
              >
                <span className="flex items-center">
                  <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" /> 
                  {currentLabel}
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] max-h-80 overflow-y-auto">
              {ranges.map(range => (
                <DropdownMenuItem key={range.label} onSelect={() => handlePresetSelect(range)}>
                  {range.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {monthOptions.map(monthOption => (
                <DropdownMenuItem key={monthOption.label} onSelect={() => handleMonthSelect(monthOption)}>
                  {monthOption.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => { 
                 // Initialize calendar with current range if available
                 setCustomRange(startDate && endDate ? { from: startDate, to: endDate } : undefined);
                 setShowCalendar(true);
               }}>
                Custom Range...
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleClear} className="text-red-600 focus:text-red-700 focus:bg-red-50">
                Clear Date
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
       ) : (
          // --- Calendar View --- 
          <div className="border rounded-md p-3 bg-gray-50 flex flex-col items-center">
             <Calendar
               mode="range"
               selected={customRange}
               onSelect={setCustomRange}
               numberOfMonths={1} // Show one month at a time
               initialFocus
             />
             <div className="flex justify-end gap-2 mt-3 w-full">
                <Button variant="ghost" size="sm" onClick={handleCancelCustomRange}>Cancel</Button>
                <Button 
                  size="sm"
                  onClick={handleApplyCustomRange} 
                  disabled={!customRange?.from || !customRange?.to}
                  className="bg-[#8A6FE8] hover:bg-[#7A5FD8] text-white disabled:opacity-50"
                 >Apply</Button>
             </div>
          </div>
       )}
     </div>
  );
};
// --- End Updated Date Range Dropdown Placeholder ---

interface SideFilterSheetProps {
  isVisible?: boolean;
  onToggleVisibility?: () => void;
  filters: Record<string, string[]>;
  filterOptions: Record<string, string[]>;
  handleFilterChange: (field: string, value: string, checked: boolean) => void;
  clearAllFilters: () => void;
  savedFilters: Record<string, Record<string, string[]>>;
  currentFilterName: string;
  newFilterName: string;
  setNewFilterName: (name: string) => void;
  saveCurrentFilter: () => void;
  loadSavedFilter: (name: string) => void;
  setDefaultFilter: (name: string) => void;
  deleteSavedFilter: (name: string) => void;
  dateRange?: { startDate?: string; endDate?: string };
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  clearDateRange: () => void;
  filteredCount: number;
  optionCounts?: Record<string, Record<string, number>>;
  dynamicCounts?: Record<string, Record<string, number>>;
  isMobileSheet?: boolean;
  hideCreateNew?: boolean;
  viewMode?: 'list' | 'grid';
  onViewModeChange?: (mode: 'list' | 'grid') => void;
  showViewSelector?: boolean;
  groupByUrl?: boolean;
  onGroupByUrlChange?: (grouped: boolean) => void;
  columnVisibility?: ColumnConfig[];
  onColumnVisibilityChange?: (columns: ColumnConfig[]) => void;
}

const MAX_OPTIONS_VISIBLE = 5;

export const SideFilterSheet = ({
  isVisible = true,
  onToggleVisibility,
  filters,
  filterOptions,
  handleFilterChange,
  clearAllFilters,
  savedFilters,
  currentFilterName,
  newFilterName,
  setNewFilterName,
  saveCurrentFilter,
  loadSavedFilter,
  setDefaultFilter,
  deleteSavedFilter,
  dateRange = {},
  onDateRangeChange,
  clearDateRange,
  filteredCount,
  optionCounts = realFilterCounts,
  dynamicCounts,
  isMobileSheet,
  hideCreateNew = false,
  viewMode,
  onViewModeChange,
  showViewSelector = true,
  groupByUrl,
  onGroupByUrlChange,
  columnVisibility,
  onColumnVisibilityChange,
}: SideFilterSheetProps) => {

  // Minimal debug logging for development only
  if (process.env.NODE_ENV === 'development') {
    console.log("[SideFilterSheet] Rendered with", {
      filterCount: Object.keys(filters).length,
      optionCount: Object.keys(filterOptions).length,
      hasDynamicCounts: !!dynamicCounts
    });
  }

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const handleReset = useCallback(() => {
    clearAllFilters();
    setExpandedCategories({});
    setPickerStartDate(undefined);
    setPickerEndDate(undefined);
  }, [clearAllFilters]);

  const [pickerStartDate, setPickerStartDate] = useState<Date | undefined>(
    dateRange.startDate ? new Date(dateRange.startDate) : undefined
  );
  const [pickerEndDate, setPickerEndDate] = useState<Date | undefined>(
    dateRange.endDate ? new Date(dateRange.endDate) : undefined
  );

  React.useEffect(() => {
    setPickerStartDate(dateRange.startDate ? new Date(dateRange.startDate) : undefined);
    setPickerEndDate(dateRange.endDate ? new Date(dateRange.endDate) : undefined);
  }, [dateRange.startDate, dateRange.endDate]);

  const handleDateApply = useCallback((start?: Date, end?: Date, label?: string) => {
    if (start && end && onDateRangeChange) {
      onDateRangeChange(start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
    } else {
      clearDateRange();
    }
    setPickerStartDate(start);
    setPickerEndDate(end);
  }, [onDateRangeChange, clearDateRange]);

  const handleInternalDateClear = useCallback(() => {
    clearDateRange();
    setPickerStartDate(undefined);
    setPickerEndDate(undefined);
  }, [clearDateRange]);

  const totalActiveFilters = useMemo(() => {
    const categoryFiltersCount = typeof filters === 'object' && filters !== null 
        ? Object.values(filters)
            .filter(val => val !== undefined)
            .reduce((acc, curr) => acc + (Array.isArray(curr) ? curr.length : 0), 0)
        : 0;
    const dateFilterActive = !!(dateRange.startDate && dateRange.endDate);
    return categoryFiltersCount + (dateFilterActive ? 1 : 0);
  }, [filters, dateRange]);

  const filterCategories = useMemo(() => {
    // Define the desired order of categories (after the date range which is already at the top)
    const desiredOrder = ['brandName', 'sentiment', 'category', 'visibility', 'source', 'country'];
    
    // Get all available keys from filterOptions
    const availableKeys = Object.keys(filterOptions);
    
    // Create a normalized version of availableKeys for case-insensitive comparison
    const normalizedKeyMap: Record<string, string> = {};
    availableKeys.forEach(key => {
      normalizedKeyMap[key.toLowerCase()] = key;
    });
    
    // Function to find the actual key for each desired category
    const findKeyForCategory = (category: string): string | undefined => {
      // Direct match
      if (availableKeys.includes(category)) {
        return category;
      }
      
      // Case-insensitive match
      const normalized = category.toLowerCase();
      if (normalized in normalizedKeyMap) {
        return normalizedKeyMap[normalized];
      }
      
      return undefined;
    };
    
    // Build ordered list according to desired order
    const orderedKeys: string[] = [];
    
    // Add keys in desired order
    for (const category of desiredOrder) {
      const key = findKeyForCategory(category);
      if (key) {
        orderedKeys.push(key);
      }
    }
    
    // Add any remaining keys not in the ordered list
    availableKeys.forEach(key => {
      // Skip 'brand' since we're using 'brandName' consistently
      if (!orderedKeys.includes(key) && key !== 'brand') {
        orderedKeys.push(key);
      }
    });
    

    return orderedKeys;
  }, [filterOptions]);

  // Function to get display name for a category
  const getCategoryDisplayName = useCallback((category: string): string => {
    const lowerCategory = category.toLowerCase();
    
    // Special case for brand/brandName variations
    if (lowerCategory === 'brandname') {
      return 'Brand';
    }
    
    // Special case for visibility
    if (lowerCategory === 'visibility') {
      return 'Visibility';
    }

    // Special case for country
    if (lowerCategory === 'country') {
      return 'Country';
    }
    
    // Default capitalization for other categories
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  }, []);

  // Helper function to normalize category keys for counts
  const normalizeCategoryKey = useCallback((category: string): string => {
    const lowercaseCategory = category.toLowerCase();
    
    // Mapping for known category variations
    const categoryMap: Record<string, string> = {
      'brandname': 'brand',
      'brand': 'brand',
      'categories': 'category',
      'category': 'category', 
      'sources': 'source',
      'source': 'source',
      'sentiments': 'sentiment',
      'sentiment': 'sentiment',
      'visibility': 'visibility',
      'country': 'country'
    };
    
    // Use mapping if available, otherwise use lowercase
    const result = categoryMap[lowercaseCategory] || lowercaseCategory;
    

    
    return result;
  }, []);

  // Helper function to get the correct case-sensitive key from dynamicCounts
  const findDynamicCountKey = useCallback((searchKey: string): string | null => {
    if (!dynamicCounts) return null;
    
    // Direct match
    if (searchKey in dynamicCounts) return searchKey;
    
    // Case-insensitive search
    const lowerSearchKey = searchKey.toLowerCase();
    for (const key of Object.keys(dynamicCounts)) {
      if (key.toLowerCase() === lowerSearchKey) {
        return key;
      }
    }
    
    // No match found
    return null;
  }, [dynamicCounts]);
  
  // Memoized category key mapping to avoid repeated calculations
  const categoryKeyMap = useMemo(() => ({
    'brandname': 'brand',
    'brand': 'brand',
    'category': 'category',
    'sentiment': 'sentiment',
    'source': 'source',
    'visibility': 'visibility',
    'country': 'country'
  } as Record<string, string>), []);

  // Memoized count cache to avoid repeated lookups
  const countCache = useMemo(() => {
    const cache = new Map<string, number>();
    
    if (!dynamicCounts) return cache;
    
    // Pre-populate cache with all available counts
    Object.entries(dynamicCounts).forEach(([categoryKey, counts]) => {
      Object.entries(counts).forEach(([option, count]) => {
        cache.set(`${categoryKey}:${option}`, count as number);
      });
    });
    
    return cache;
  }, [dynamicCounts]);

  // Optimized function to get count for an option
  const getOptionCount = useCallback((category: string, option: string): number => {
    // Map the category to the appropriate key
    const countKey = categoryKeyMap[category.toLowerCase()] || category.toLowerCase();
    
    // Check cache first
    const cacheKey = `${countKey}:${option}`;
    const cachedCount = countCache.get(cacheKey);
    if (cachedCount !== undefined) {
      return cachedCount;
    }
    
    // Hardcoded fallbacks for specific cases
    if (option === "Nikos Christodoulides" && countKey === 'brand') {
      return 96;
    }
    
    if (option === "Pizza Hut" && countKey === 'brand') {
      return 150;
    }
    
    // Fallback to static counts if available
    if (optionCounts && countKey in optionCounts && option in optionCounts[countKey]) {
      return optionCounts[countKey][option];
    }
    
    // If still no count found, return 0
    return 0;
  }, [categoryKeyMap, countCache, optionCounts]);

  // Calculate total filter counts for display at the bottom
  const totalFilteredItems = useMemo(() => {
    // If we have an explicit filteredCount from props, use it directly
    if (typeof filteredCount === 'number') {
      return filteredCount;
    }
    
    return 0;
  }, [filteredCount]);

  const toggleExpandCategory = (category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Function to prioritize and sort options with selected filters first
  const getSortedOptions = useCallback((category: string, options: string[]) => {
    const selectedOptions = (typeof filters === 'object' && filters !== null && Array.isArray(filters[category]))
      ? filters[category]
      : [];
    
    // Get all available options from the filterOptions prop
    let allOptions = [...options];
    
    // Sort options: selected first, then by count descending (0 counts last), then alphabetically
    return allOptions.sort((a, b) => {
      const aSelected = selectedOptions.includes(a);
      const bSelected = selectedOptions.includes(b);
      
      // Primary sort: selected items first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      // Get counts, defaulting to 0 if undefined
      const countA = getOptionCount(category, a) ?? 0;
      const countB = getOptionCount(category, b) ?? 0;
      
      // Secondary sort: by count descending (items with count > 0 first)
      if ((countA > 0 && countB === 0) || (countB > 0 && countA === 0)) {
        return countB - countA; // Put options with counts > 0 first
      }
      
      // If both have counts or both have no counts, sort by count value
      if (countA !== countB) {
        return countB - countA;
      }
      
      // Tertiary sort: alphabetically
      return a.localeCompare(b);
    });
  }, [filters, getOptionCount]);

  // Function to determine visible options ensuring selected options are always visible
  const getVisibleOptions = useCallback((category: string, options: string[]) => {
    const isExpanded = expandedCategories[category] || false;
    if (isExpanded) return options; // Show all when expanded
    
    const selectedOptions = (typeof filters === 'object' && filters !== null && Array.isArray(filters[category]))
      ? filters[category]
      : [];
    
    // If we have selected options, ensure they're visible
    if (selectedOptions.length > 0) {
      // First include all selected options
      const visibleSelected = options.filter(opt => selectedOptions.includes(opt));
      
      // Then add non-selected options until we reach MAX_OPTIONS_VISIBLE
      const remainingSlots = MAX_OPTIONS_VISIBLE - visibleSelected.length;
      if (remainingSlots > 0) {
        const nonSelectedOptions = options.filter(opt => !selectedOptions.includes(opt)).slice(0, remainingSlots);
        return [...visibleSelected, ...nonSelectedOptions];
      }
      return visibleSelected.slice(0, MAX_OPTIONS_VISIBLE);
    }
    
    // Default to first MAX_OPTIONS_VISIBLE options when no selection
    return options.slice(0, MAX_OPTIONS_VISIBLE);
  }, [expandedCategories, filters]);

  // Simplified validation for development (removed heavy logging)
  useEffect(() => {
    // Only validate in development and when we have data
    if (process.env.NODE_ENV === 'development' && dynamicCounts && Object.keys(dynamicCounts).length > 0) {
      // Basic validation without heavy logging
      const hasValidCounts = Object.values(dynamicCounts).some(counts => 
        Object.values(counts).some(count => (count as number) > 0)
      );
      if (!hasValidCounts) {
        console.warn("[SideFilterSheet] No valid counts found in dynamicCounts");
      }
    }
  }, [dynamicCounts]);









  // Wrapped version of handleFilterChange
  const handleFilterChangeWrapper = useCallback((field: string, value: string, checked: boolean) => {
    handleFilterChange(field, value, checked);
  }, [handleFilterChange]);

  return (
    <div className={`h-full bg-white border-r border-gray-200 flex flex-col ${
      isVisible ? 'block' : 'hidden'
    } ${isMobileSheet ? 'w-full' : 'w-[240px] lg:w-[300px] flex-shrink-0'}`}>
      {/* Header - Hide in mobile sheet mode since it has its own header */}
      {!isMobileSheet && (
        <div className="px-4 lg:px-6 pt-4 pb-3 flex flex-row justify-between items-center border-b bg-white">
          <h2 className="text-xl lg:text-lg font-semibold text-gray-900">Filter by</h2>
          <div className="flex items-center gap-2">
            <Button variant="link" onClick={handleReset} className="text-base lg:text-sm p-0 h-auto text-[#8A6FE8] hover:text-[#7A5FD8]">
              Reset
            </Button>
            {onToggleVisibility && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onToggleVisibility}
                className="p-1 h-auto text-gray-500 hover:text-gray-700 lg:hidden"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Mobile sheet header */}
      {isMobileSheet && (
        <div className="px-4 pt-4 pb-3 flex flex-row justify-between items-center border-b bg-white">
          <Button variant="link" onClick={handleReset} className="text-base lg:text-sm p-0 h-auto text-[#8A6FE8] hover:text-[#7A5FD8]">
            Reset All
          </Button>
        </div>
      )}

      {/* Content */}
      <ScrollArea className="flex-grow px-4 lg:px-6 pt-4">
        {/* View Selector */}
        {showViewSelector && viewMode && onViewModeChange && (
          <div className="mb-4">
            <h3 className="text-sm lg:text-xs font-medium text-gray-700 mb-2">View</h3>
            <div className="flex rounded-md border border-gray-200 bg-gray-50 p-1">
              <button
                onClick={() => onViewModeChange('grid')}
                className={`flex-1 flex items-center justify-center px-3 py-2 text-sm rounded transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid className="h-4 w-4 mr-1.5" />
                Grid
              </button>
              <button
                onClick={() => onViewModeChange('list')}
                className={`flex-1 flex items-center justify-center px-3 py-2 text-sm rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="h-4 w-4 mr-1.5" />
                List
              </button>
            </div>
          </div>
        )}

        {/* URL Grouping Toggle */}
        {onGroupByUrlChange && (
          <div className="mb-4">
            <h3 className="text-sm lg:text-xs font-medium text-gray-700 mb-2">Display</h3>
            <div className="flex items-center space-x-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={groupByUrl || false}
                  onChange={(e) => onGroupByUrlChange(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#8A6FE8]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#8A6FE8]"></div>
                <span className="ml-3 text-sm text-gray-700">Group by URL</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">Combine articles with the same URL into single entries</p>
          </div>
        )}

        {/* Column Visibility Filter - only show in list view */}
        {onColumnVisibilityChange && columnVisibility && viewMode === 'list' && (
          <div className="mb-4">
            <h3 className="text-sm lg:text-xs font-medium text-gray-700 mb-2">Table Columns</h3>
            <ColumnVisibilityFilter
              columns={columnVisibility}
              onColumnsChange={onColumnVisibilityChange}
            />
          </div>
        )}

        <DateRangeDropdownPicker
          startDate={pickerStartDate}
          endDate={pickerEndDate}
          onApply={handleDateApply}
          onClear={handleInternalDateClear} 
        />

        <Separator className="mb-6 mt-6" />

        {filterCategories.map((category, index) => {
          const actualCategory = category;
          const allOptions = filterOptions[actualCategory] || [];
          const sortedOptions = getSortedOptions(actualCategory, allOptions);
          const visibleOptions = getVisibleOptions(actualCategory, sortedOptions);
          const needsExpansionControl = allOptions.length > MAX_OPTIONS_VISIBLE; 
          const isExpanded = expandedCategories[category] || false;

          return (
            <div key={category} className="mb-6">
              <h3 className="text-lg lg:text-base font-semibold mb-3 text-gray-800">
                {getCategoryDisplayName(category)}
              </h3>
              <div className="space-y-3">
                {visibleOptions.map((option) => {
                  const isChecked = typeof filters === 'object' && filters !== null && Array.isArray(filters[actualCategory])
                    ? filters[actualCategory].includes(option)
                    : false;
                  const label = option;
                  const count = getOptionCount(actualCategory, option);
                  


                  return (
                    <div key={option} className="flex items-center justify-between">
                      <label htmlFor={`${actualCategory}-${option}`} className="flex items-center space-x-2 text-sm lg:text-xs cursor-pointer grow pr-2 text-gray-700">
                        <Checkbox
                          id={`${actualCategory}-${option}`}
                          checked={isChecked}
                          onCheckedChange={(checked) => handleFilterChangeWrapper(actualCategory, option, !!checked)}
                          className="mr-2 border-gray-300 data-[state=checked]:bg-[#8A6FE8] data-[state=checked]:border-[#8A6FE8]"
                        />
                        <span className="flex-grow text-base lg:text-sm">
                          {label}
                          {/* Add clickable URL for countries */}
                          {actualCategory.toLowerCase() === 'country' && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Create a search URL for the country's media outlets
                                const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(option + ' media outlets news websites')}`;
                                window.open(searchUrl, '_blank', 'noopener,noreferrer');
                              }}
                              className="ml-2 text-[#8A6FE8] hover:text-[#7A5FD8] hover:underline text-xs"
                              title={`Search for ${option} media outlets`}
                            >
                              🔗
                            </button>
                          )}
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>

              {needsExpansionControl && !isExpanded && (
                <Button 
                  variant="link"
                  onClick={() => toggleExpandCategory(category)}
                  className="p-0 h-auto mt-2 text-base lg:text-sm text-[#8A6FE8] hover:text-[#7A5FD8] flex items-center"
                >
                  Show more ({allOptions.length - MAX_OPTIONS_VISIBLE})
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              )}

              {needsExpansionControl && isExpanded && (
                <Button 
                  variant="link"
                  onClick={() => toggleExpandCategory(category)}
                  className="p-0 h-auto mt-2 text-base lg:text-sm text-[#8A6FE8] hover:text-[#7A5FD8] flex items-center"
                >
                  Show less
                  <ChevronUp className="h-4 w-4 ml-1" />
                </Button>
              )}

              {index < filterCategories.length - 1 && <Separator className="mt-6" />}
            </div>
          );
        })}
      </ScrollArea>

      {/* Create New Button Section */}
      {!hideCreateNew && (
        <div className="px-4 lg:px-6 py-3 bg-gray-50 border-t">
          <Button 
            onClick={() => {
              // Emit a custom event that pages can listen to
              window.dispatchEvent(new CustomEvent('create-new-clicked'));
            }}
            className="w-full bg-[#8A6FE8] hover:bg-[#7A5FD8] text-white font-medium py-2.5 rounded-md transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create New
          </Button>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 lg:px-6 py-4 bg-white border-t">
        <div className="text-center text-sm lg:text-xs text-gray-600 font-medium">
          Showing {new Intl.NumberFormat('en-US').format(filteredCount)} results
        </div>
      </div>
    </div>
  );
}; 

export { MobileFilterBar }; 